/**
 * Generated by orval v7.21.0 🍺
 * Do not edit manually.
 * Mission Impact Auth API
 * OpenAPI spec version: v1
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  CreateRoleRequest,
  CreateRoleResponse,
  ProblemDetails,
  UpdateRoleRequest,
  UserRoleResponse,
  UserRolesListResponse,
} from '../missionImpactAuthAPI.schemas';

import { instance } from '../../mutator-auth';
import type { ErrorType } from '../../mutator-auth';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

/**
 * Creates a new role assignment for a user. Either userId or email must be provided, but not both. Requires Administrator role in the organization.
 * @summary Create role assignment
 */
export const createRole = (
  orgId: string,
  createRoleRequest: CreateRoleRequest,
  options?: SecondParameter<typeof instance>,
  signal?: AbortSignal
) => {
  return instance<CreateRoleResponse>(
    {
      url: `/v1/org/${orgId}/roles`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: createRoleRequest,
      signal,
    },
    options
  );
};

export const getCreateRoleMutationOptions = <
  TError = ErrorType<ProblemDetails>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createRole>>,
    TError,
    { orgId: string; data: CreateRoleRequest },
    TContext
  >;
  request?: SecondParameter<typeof instance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createRole>>,
  TError,
  { orgId: string; data: CreateRoleRequest },
  TContext
> => {
  const mutationKey = ['createRole'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createRole>>,
    { orgId: string; data: CreateRoleRequest }
  > = (props) => {
    const { orgId, data } = props ?? {};

    return createRole(orgId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateRoleMutationResult = NonNullable<Awaited<ReturnType<typeof createRole>>>;
export type CreateRoleMutationBody = CreateRoleRequest;
export type CreateRoleMutationError = ErrorType<ProblemDetails>;

/**
 * @summary Create role assignment
 */
export const useCreateRole = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createRole>>,
      TError,
      { orgId: string; data: CreateRoleRequest },
      TContext
    >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof createRole>>,
  TError,
  { orgId: string; data: CreateRoleRequest },
  TContext
> => {
  const mutationOptions = getCreateRoleMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Returns all roles for a specific organization. Requires Administrator role in the organization. Each role includes invitation state, invitation expiration, and membership expiration timestamps.
 * @summary Get organization roles
 */
export const getOrganizationRoles = (
  orgId: string,
  options?: SecondParameter<typeof instance>,
  signal?: AbortSignal
) => {
  return instance<UserRolesListResponse>(
    { url: `/v1/org/${orgId}/roles`, method: 'GET', signal },
    options
  );
};

export const getGetOrganizationRolesQueryKey = (orgId?: string) => {
  return [`/v1/org/${orgId}/roles`] as const;
};

export const getGetOrganizationRolesQueryOptions = <
  TData = Awaited<ReturnType<typeof getOrganizationRoles>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getOrganizationRoles>>, TError, TData>
    >;
    request?: SecondParameter<typeof instance>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetOrganizationRolesQueryKey(orgId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getOrganizationRoles>>> = ({ signal }) =>
    getOrganizationRoles(orgId, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!orgId, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getOrganizationRoles>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetOrganizationRolesQueryResult = NonNullable<
  Awaited<ReturnType<typeof getOrganizationRoles>>
>;
export type GetOrganizationRolesQueryError = ErrorType<ProblemDetails>;

export function useGetOrganizationRoles<
  TData = Awaited<ReturnType<typeof getOrganizationRoles>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getOrganizationRoles>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOrganizationRoles>>,
          TError,
          Awaited<ReturnType<typeof getOrganizationRoles>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetOrganizationRoles<
  TData = Awaited<ReturnType<typeof getOrganizationRoles>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getOrganizationRoles>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOrganizationRoles>>,
          TError,
          Awaited<ReturnType<typeof getOrganizationRoles>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetOrganizationRoles<
  TData = Awaited<ReturnType<typeof getOrganizationRoles>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getOrganizationRoles>>, TError, TData>
    >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get organization roles
 */

export function useGetOrganizationRoles<
  TData = Awaited<ReturnType<typeof getOrganizationRoles>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getOrganizationRoles>>, TError, TData>
    >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetOrganizationRolesQueryOptions(orgId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Updates an existing role assignment including role, invitation state, invitation expiration, and membership expiration. Can be used for re-inviting users (set invitationState to Invited). Requires Administrator role in the organization.
 * @summary Update role assignment
 */
export const updateRole = (
  orgId: string,
  roleId: string,
  updateRoleRequest: UpdateRoleRequest,
  options?: SecondParameter<typeof instance>
) => {
  return instance<UserRoleResponse>(
    {
      url: `/v1/org/${orgId}/roles/${roleId}`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: updateRoleRequest,
    },
    options
  );
};

export const getUpdateRoleMutationOptions = <
  TError = ErrorType<ProblemDetails>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateRole>>,
    TError,
    { orgId: string; roleId: string; data: UpdateRoleRequest },
    TContext
  >;
  request?: SecondParameter<typeof instance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateRole>>,
  TError,
  { orgId: string; roleId: string; data: UpdateRoleRequest },
  TContext
> => {
  const mutationKey = ['updateRole'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateRole>>,
    { orgId: string; roleId: string; data: UpdateRoleRequest }
  > = (props) => {
    const { orgId, roleId, data } = props ?? {};

    return updateRole(orgId, roleId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateRoleMutationResult = NonNullable<Awaited<ReturnType<typeof updateRole>>>;
export type UpdateRoleMutationBody = UpdateRoleRequest;
export type UpdateRoleMutationError = ErrorType<ProblemDetails>;

/**
 * @summary Update role assignment
 */
export const useUpdateRole = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateRole>>,
      TError,
      { orgId: string; roleId: string; data: UpdateRoleRequest },
      TContext
    >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof updateRole>>,
  TError,
  { orgId: string; roleId: string; data: UpdateRoleRequest },
  TContext
> => {
  const mutationOptions = getUpdateRoleMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Deletes a role assignment. Requires Administrator role in the organization.
 * @summary Delete role assignment
 */
export const deleteRole = (
  orgId: string,
  roleId: string,
  options?: SecondParameter<typeof instance>
) => {
  return instance<void>({ url: `/v1/org/${orgId}/roles/${roleId}`, method: 'DELETE' }, options);
};

export const getDeleteRoleMutationOptions = <
  TError = ErrorType<ProblemDetails>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteRole>>,
    TError,
    { orgId: string; roleId: string },
    TContext
  >;
  request?: SecondParameter<typeof instance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteRole>>,
  TError,
  { orgId: string; roleId: string },
  TContext
> => {
  const mutationKey = ['deleteRole'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteRole>>,
    { orgId: string; roleId: string }
  > = (props) => {
    const { orgId, roleId } = props ?? {};

    return deleteRole(orgId, roleId, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteRoleMutationResult = NonNullable<Awaited<ReturnType<typeof deleteRole>>>;

export type DeleteRoleMutationError = ErrorType<ProblemDetails>;

/**
 * @summary Delete role assignment
 */
export const useDeleteRole = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteRole>>,
      TError,
      { orgId: string; roleId: string },
      TContext
    >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof deleteRole>>,
  TError,
  { orgId: string; roleId: string },
  TContext
> => {
  const mutationOptions = getDeleteRoleMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
